import {
  addDays,
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  differenceInDays,
  differenceInMonths,
  differenceInQuarters,
  differenceInWeeks,
  differenceInYears,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  parseISO,
  setYear,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
  isFirstDayOfMonth,
  isSameDay,
  getMonth,
  parse,
  startOfYesterday,
} from "date-fns";
import {
  DATE_FILTER_MODE_RANGE_ONLY,
  DATE_RANGE_START_CUSTOM,
  rangeAndAggregationOptionsByValue,
  daysOfWeek,
  dateFiltersTypeDependency,
  DATE_RANGE_END_CUSTOM,
} from "../constants/constants";
import { absoluteDate } from "../dates/dateFunc";

const pageLoadDate = new Date();
const pageLoadDateString = pageLoadDate.toISOString().slice(0, 10);

const isStagingEnv = process.env.REACT_APP_API_ENDPOINT?.includes("staging");

export const fromToDateFormatter = (term, range = 3) => {
  const dateRange = { from: null, to: null };
  switch (term) {
    case "monthly": {
      dateRange.to = format(endOfMonth(Date.now()), "yyyy-MM-dd");
      dateRange.from = format(
        startOfMonth(subMonths(Date.now(), range)),
        "yyyy-MM-dd"
      );
      break;
    }

    case "quarterly": {
      dateRange.to = format(endOfQuarter(Date.now()), "yyyy-MM-dd");
      dateRange.from = format(
        startOfQuarter(subQuarters(Date.now(), range)),
        "yyyy-MM-dd"
      );
      break;
    }

    case "weekly": {
      dateRange.to = format(endOfWeek(Date.now()), "yyyy-MM-dd");
      dateRange.from = format(
        subWeeks(endOfWeek(Date.now()), range),
        "yyyy-MM-dd"
      );
      break;
    }

    case "daily": {
      dateRange.to = format(Date.now(), "yyyy-MM-dd");
      dateRange.from = format(subDays(Date.now(), range), "yyyy-MM-dd");
      break;
    }

    default: {
      dateRange.to = format(endOfYear(Date.now()), "yyyy-MM-dd");
      dateRange.from = format(
        startOfYear(subYears(Date.now(), range)),
        "yyyy-MM-dd"
      );
    }
  }

  return dateRange;
};

const returnEmpty = (dateFilters) => {
  if (
    dateFilters.globalMaximumDate &&
    dateFilters.globalMinimumDate &&
    dateFilters.rangeSettingsStart
  ) {
    return false;
  }

  return (
    !dateFilters ||
    (dateFilters.filterMode !== DATE_FILTER_MODE_RANGE_ONLY &&
      !dateFilters.filterCustomValues?.find((fcv) =>
        dateFiltersTypeDependency.includes(fcv)
      )) ||
    !dateFilters.rangeSettingsTerm ||
    (!Number.isInteger(dateFilters.rangeSettingsStart) &&
      dateFilters.rangeSettingsStart !== DATE_RANGE_START_CUSTOM) ||
    !Number.isInteger(dateFilters.rangeSettingsEnd)
  );
};

export const createDateOptions = (dateFilters) => {
  if (returnEmpty(dateFilters)) {
    return [];
  }

  const term = dateFilters.rangeSettingsTerm;

  const startDate = absoluteDate(
    dateFilters.rangeSettingsStart === DATE_RANGE_START_CUSTOM
      ? dateFilters.globalMinimumDate
      : startOfNowTermForOptions(dateFilters.rangeSettingsStart, term)
  );

  const endDate = absoluteDate(
    dateFilters.rangeSettingsEnd === DATE_RANGE_END_CUSTOM
      ? dateFilters.globalMaximumDate
      : startOfNowTermForOptions(dateFilters.rangeSettingsEnd, term)
  );

  const config = {
    monthly: {
      count: differenceInMonths(endDate, startDate) + 1,
      add: addMonths,
    },
    quarterly: {
      count: differenceInQuarters(endDate, startDate) + 1,
      add: addQuarters,
    },
    yearly: {
      count: differenceInYears(endDate, startDate) + 1,
      add: addYears,
    },
    weekly: {
      count: differenceInWeeks(endDate, startDate) + 1,
      add: addWeeks,
    },
    daily: {
      count: differenceInDays(endDate, startDate) + 1,
      add: addDays,
    },
  };

  return Array(config[dateFilters.rangeSettingsTerm].count)
    .fill(null)
    .reduce((acc, _, i) => {
      const date = config[dateFilters.rangeSettingsTerm].add(startDate, i);
      const value = format(date, "yyyy-MM-dd");
      const label = format(
        date,
        rangeAndAggregationOptionsByValue[dateFilters.rangeSettingsTerm]
          .displayFormat
      );

      return [...acc, { value, label }];
    }, []);
};

export const startOfNowTermForOptions = (range, term) => {
  const start = {
    monthly: startOfMonth,
    quarterly: startOfQuarter,
  };
  const fn = start[term] || startOfQuarter;
  const d = fn(Date.now());
  return format(
    range >= 0 ? addMonths(d, range) : subMonths(d, -range),
    "yyyy-MM-dd"
  );
};

export const dateRangeType = {
  RM: "rm",
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
  YEARLY: "yearly",
  WEEK_TO_DATE: "wtd",
  MONTH_TO_DATE: "mtd",
  QUARTER_TO_DATE: "qtd",
  YEAR_TO_DATE: "ytd",
  CURRENT_SCHOOL_YEAR: "csy",
  PREVIOUS_SCHOOL_YEAR: "psy",
  LAST_TWELVE_MONTHS: "ltm",
  YESTERDAY: "yesterday",
  TODAY: "today",
  LAST_WEEK: "lw",
  NINETY_DAYS: "90days",
  SINGLE_QUARTER: "singleQuarter",
  ONE_YEAR: "oneYear",
  PREVIOUS_YEAR_TO_DATE: "previousYearToDate",
  TWO_YEARS_AGO_TO_DATE: "twoYearsAgoToDate",
  CURRENT_PREVIOUS_YEAR: "currPrevYear",
  CURRENT_PREVIOUS_TWO_YEARS_AGO: "currPrevTwoYearsAgo",
  NONE: "none",
  THIS_YEAR: "thisYear",
  THIS_QUARTER: "thisQuarter",
  THIS_YEAR_TO_END_OF_QUARTER: "thisYearToEndOfQuarter",
};

export const dateConfig = {
  defaultDateString: pageLoadDateString,
  defaultDate: pageLoadDate,
  setDefaultDate(date) {
    this.defaultDateString = format(date, "yyyy-MM-dd");
    this.defaultDate = date;
  },
};

/**
 * Gets the current date (when the page was first loaded), or potentially a
 * stubbed date for tests.
 *
 * @returns {Date}
 */
function getDate() {
  return dateConfig.defaultDate;
}

export const setDateFilters = (
  domain,
  term,
  rangeSettingsRange = 0,
  start = dateConfig.defaultDateString,
  end = dateConfig.defaultDateString,
  config = {}
) => {
  function applyTodayOffset() {
    return domain === "ccg" ? setSpecificTimezone() : end;
  }

  switch (term) {
    case dateRangeType.RM: {
      const approved = config.approved?.rm;

      if (approved) {
        const approvedMonth = new parse(approved, "MMMM-yyyy", getDate());

        return {
          from: format(startOfMonth(approvedMonth), "yyyy-MM-dd"),
          to: format(endOfMonth(approvedMonth), "yyyy-MM-dd"),
        };
      }

      const currentMonth = getDate();

      return {
        from: format(subMonths(startOfMonth(currentMonth), 1), "yyyy-MM-dd"),
        to: format(subMonths(endOfMonth(currentMonth), 1), "yyyy-MM-dd"),
      };
    }

    case dateRangeType.MONTHLY: {
      return {
        from: format(startOfMonth(parseISO(start)), "yyyy-MM-dd"),
        to: format(endOfMonth(parseISO(end)), "yyyy-MM-dd"),
      };
    }

    case dateRangeType.QUARTERLY: {
      return {
        from: format(startOfQuarter(parseISO(start)), "yyyy-MM-dd"),
        to: format(endOfQuarter(parseISO(end)), "yyyy-MM-dd"),
      };
    }

    case dateRangeType.YEARLY: {
      return {
        from:
          format(startOfYear(parseISO(start)), "yyyy-MM-dd") +
          "T06:00:00.000000Z",
        to:
          format(endOfYear(parseISO(end)), "yyyy-MM-dd") + "T06:00:00.000000Z",
      };
    }
    case dateRangeType.WEEK_TO_DATE: {
      return {
        from: format(startOfWeek(parseISO(start)), "yyyy-MM-dd"),
        to: format(parseISO(applyTodayOffset()), "yyyy-MM-dd"),
      };
    }

    case dateRangeType.MONTH_TO_DATE: {
      function applyStartTodayOffset() {
        const isFirstOfMonth = isFirstDayOfMonth(absoluteDate(end));
        if (domain === "ccg" && isFirstOfMonth) {
          return parseISO(subMonths(absoluteDate(start), 1).toISOString());
        }

        return startOfMonth(parseISO(start));
      }

      const appliedStartToday = applyStartTodayOffset();
      const appliedToday = applyTodayOffset();

      return {
        from: format(appliedStartToday, "yyyy-MM-dd"),
        to: format(parseISO(appliedToday), "yyyy-MM-dd"),
      };
    }

    case dateRangeType.QUARTER_TO_DATE: {
      function applyStartTodayOffset() {
        const isDateSameDay = isSameDay(
          startOfQuarter(absoluteDate(end)),
          absoluteDate(end)
        );
        if (domain === "ccg" && isDateSameDay) {
          return parseISO(
            startOfQuarter(subQuarters(absoluteDate(start), 1)).toISOString()
          );
        }

        return startOfQuarter(absoluteDate(start));
      }

      return {
        from: format(applyStartTodayOffset(), "yyyy-MM-dd"),
        to: format(parseISO(applyTodayOffset()), "yyyy-MM-dd"),
      };
    }

    case dateRangeType.YEAR_TO_DATE: {
      return {
        from: format(startOfYear(parseISO(start)), "yyyy-MM-dd"),
        to: format(parseISO(applyTodayOffset()), "yyyy-MM-dd"),
      };
    }
    // Current school year which starts with september
    case dateRangeType.CURRENT_SCHOOL_YEAR: {
      const date = getDate();
      const currentYear = date.getFullYear();
      const thisYearSeptember = new Date(currentYear, 8, 1);
      const prevYearSeptember = new Date(currentYear - 1, 8, 1);

      const start =
        thisYearSeptember.getTime() < date.getTime()
          ? thisYearSeptember
          : prevYearSeptember;

      return {
        from: format(start, "yyyy-MM-dd"),
        to: format(parseISO(applyTodayOffset()), "yyyy-MM-dd"),
      };
    }
    // Prev school year which starts with september prev year
    case dateRangeType.PREVIOUS_SCHOOL_YEAR: {
      const date = getDate();
      const currentYear = date.getFullYear();
      const yearOffset =
        new Date(currentYear, 8, 1).getTime() < date.getTime() ? 1 : 2;
      const start = new Date(currentYear - yearOffset, 8, 1);
      const end = new Date(currentYear, 7, 31);

      return {
        from: format(start, "yyyy-MM-dd"),
        to: format(end, "yyyy-MM-dd"),
      };
    }

    case dateRangeType.LAST_TWELVE_MONTHS: {
      return {
        to: format(endOfMonth(subMonths(getDate(), 1)), "yyyy-MM-dd"),
        from: format(startOfMonth(subMonths(getDate(), 12)), "yyyy-MM-dd"),
      };
    }

    case dateRangeType.YESTERDAY: {
      const date = subDays(getDate(), 1);

      if (domain === "ccg") {
        date.setUTCHours(date.getUTCHours() - 5);
      }

      return {
        from: format(date, "yyyy-MM-dd"),
        to: format(date, "yyyy-MM-dd"),
      };
    }

    case dateRangeType.TODAY: {
      const date = getDate();

      if (domain === "ccg") {
        date.setUTCHours(date.getUTCHours() - 5);
      }

      return {
        from: format(date, "yyyy-MM-dd"),
        to: format(date, "yyyy-MM-dd"),
      };
    }

    case dateRangeType.LAST_WEEK: {
      const lastWeekDate = getLastWeekDate();

      return {
        from: format(startOfWeek(lastWeekDate), "yyyy-MM-dd"),
        to: format(endOfWeek(lastWeekDate), "yyyy-MM-dd"),
      };
    }

    case dateRangeType.NINETY_DAYS: {
      return {
        from: format(subMonths(parseISO(start), 3), "yyyy-MM-dd"),
        to: format(parseISO(end), "yyyy-MM-dd"),
      };
    }

    case dateRangeType.SINGLE_QUARTER: {
      return {
        from: format(
          startOfQuarter(subQuarters(parseISO(start), rangeSettingsRange)),
          "yyyy-MM-dd"
        ),
        to: format(endOfQuarter(parseISO(end)), "yyyy-MM-dd"),
      };
    }

    case dateRangeType.ONE_YEAR:
      return {
        from: format(subYears(parseISO(start), 1), "yyyy-MM-dd"),
        to: format(parseISO(applyTodayOffset()), "yyyy-MM-dd"),
      };

    case dateRangeType.PREVIOUS_YEAR_TO_DATE:
      return {
        to: format(getDate(), "yyyy-MM-dd"),
        from: format(startOfYear(subYears(getDate(), 1)), "yyyy-MM-dd"),
      };

    case dateRangeType.TWO_YEARS_AGO_TO_DATE:
      return {
        to: format(getDate(), "yyyy-MM-dd"),
        from: format(startOfYear(subYears(getDate(), 2)), "yyyy-MM-dd"),
      };

    case dateRangeType.CURRENT_PREVIOUS_YEAR:
      return {
        to: format(endOfYear(getDate()), "yyyy-MM-dd"),
        from: format(startOfYear(subYears(getDate(), 1)), "yyyy-MM-dd"),
      };

    case dateRangeType.CURRENT_PREVIOUS_TWO_YEARS_AGO:
      return {
        to: format(endOfYear(getDate()), "yyyy-MM-dd"),
        from: format(startOfYear(subYears(getDate(), 2)), "yyyy-MM-dd"),
      };

    case dateRangeType.NONE:
      return {
        from: null,
        to: null,
      };

    case dateRangeType.THIS_YEAR:
      return {
        from: format(startOfYear(new Date()), "yyyy-MM-dd"),
        to: format(endOfYear(new Date()), "yyyy-MM-dd"),
      };

    case dateRangeType.THIS_QUARTER:
      return {
        from: format(startOfQuarter(new Date()), "yyyy-MM-dd"),
        to: format(endOfQuarter(new Date()), "yyyy-MM-dd"),
      };

    case dateRangeType.THIS_YEAR_TO_END_OF_QUARTER:
      return {
        from: format(startOfYear(new Date()), "yyyy-MM-dd"),
        to: format(endOfQuarter(new Date()), "yyyy-MM-dd"),
      };

    default: {
      return {
        from: format(parseISO(start), "yyyy-MM-dd"),
        to: format(parseISO(end), "yyyy-MM-dd"),
      };
    }
  }
};

export const getRecentMonths = ({
  valueFormat,
  labelFormat,
  isReverse,
  count,
  now = getDate(),
  hardcodedPinnacle,
}) => {
  const date = addMonths(startOfMonth(now), count + 1);
  const ignore = [];
  const { activeTab, domain } = hardcodedPinnacle ?? {};

  // remove when Pinnacle is ready
  const hide =
    domain === "pinnacle" && activeTab?.slug?.includes(pagesWithExcelence);

  const options = Array(Math.abs(count))
    .fill(null)
    .reduce((acc, _, i) => {
      return [
        ...acc,
        {
          from: format(addMonths(date, i), valueFormat),
          to: format(endOfMonth(addMonths(date, i)), valueFormat),
          label: format(addMonths(date, i), labelFormat),
        },
      ];
    }, [])
    .filter((option) => {
      if (!isStagingEnv && hide) {
        return !ignore.includes(option.label);
      }

      return true;
    });

  return isReverse ? options.reverse() : options;
};

export const getRecentQuarters = (
  valueFormat,
  labelFormat,
  isReverse,
  count,
  now = getDate()
) => {
  const date = addQuarters(startOfQuarter(now), count + 1);

  const options = Array(Math.abs(count))
    .fill(null)
    .reduce((acc, _, i) => {
      return [
        ...acc,
        {
          from: format(addQuarters(date, i), valueFormat),
          to: format(endOfQuarter(addQuarters(date, i)), valueFormat),
          label: format(addQuarters(date, i), labelFormat),
        },
      ];
    }, []);

  return isReverse ? options.reverse() : options;
};

export function getRecentWeeks(count = 26, now = getDate(), weekStartsOn = 0) {
  const date = subWeeks(startOfWeek(now, { weekStartsOn }), +count);
  const valueFormat = "yyyy-MM-dd";
  const labelFormat = "MMM d";
  const isReverse = true;

  const options = Array(+count)
    .fill(null)
    .reduce((acc, _, i) => {
      const from = addWeeks(date, i);
      const to = endOfWeek(addWeeks(date, i), { weekStartsOn });
      return [
        ...acc,
        {
          from: format(from, valueFormat),
          to: format(to, valueFormat),
          label: `${format(from, labelFormat)} - ${format(to, labelFormat)}`,
        },
      ];
    }, []);

  return isReverse ? options.reverse() : options;
}

export function getRecentYears(startYear = 2018, now = getDate()) {
  const endYear = now.getFullYear();
  const count = -(endYear - startYear + 1);
  const date = startOfYear(setYear(now, startYear));
  const valueFormat = "yyyy-MM-dd";
  const labelFormat = "yyyy";
  const isReverse = true;

  const options = Array(Math.abs(count))
    .fill(null)
    .reduce((acc, _, i) => {
      const from = addYears(date, i);
      const to = endOfYear(addYears(date, i));
      return [
        ...acc,
        {
          from: format(from, valueFormat),
          to: format(to, valueFormat),
          label: format(from, labelFormat),
        },
      ];
    }, []);

  return isReverse ? options.reverse() : options;
}

export function getRecentYearsCustom() {
  return [
    {
      label: "Current Year",
      specialLabel: new Date().getFullYear().toString(),
    },
    {
      label: "Last Year",
      specialLabel: (new Date().getFullYear() - 1).toString(),
    },
  ];
}

const getLastWeekDate = () => {
  const date = getDate();
  return date.setDate(date.getDate() - 7);
};

export function getDaysOfWeek() {
  return daysOfWeek.map((dayOfWeek) => ({
    from: dayOfWeek,
    to: dayOfWeek,
    label: dayOfWeek,
  }));
}

export function getSingleQuarters(dateFilter) {
  return createDateOptions(dateFilter).map((date) => ({
    from: format(startOfQuarter(parseISO(date.value)), "yyyy-MM-dd"),
    to: format(endOfQuarter(parseISO(date.value)), "yyyy-MM-dd"),
    label: date.label,
  }));
}

const pagesWithExcelence = "-excellence";
const ignore = ["Q3 2024", "Q4 2024"];

export function getSingleFiscalQuarters(dateFilter, hardcodedPinnacle) {
  // remove this when pinnacle Q2 2022 will be ready
  const { fiscalQuarterStartMonth } = dateFilter;
  const { activeTab, domain } = hardcodedPinnacle ?? {};

  if (!fiscalQuarterStartMonth) {
    return [];
  }

  const start = startOfYear(new Date(fiscalQuarterStartMonth));
  const end = endOfYear(getDate());
  const justMonth = getMonth(absoluteDate(fiscalQuarterStartMonth + "-01"));
  const monthOffset = justMonth / 3;
  const count = differenceInQuarters(end, start) + 1;

  let yearOffset = 0;

  const options = Array(count)
    .fill("")
    .map((_, index) => {
      const date = addMonths(start, 3 * index);
      const quarter = format(date, "Q");

      const year = format(start, "yyyy");
      const num = +quarter + monthOffset;
      const fiscal = num > 4 ? num - 4 : num;

      if (fiscal === 1) {
        yearOffset++;
      }

      return {
        from: format(startOfQuarter(date), "yyyy-MM-dd"),
        to: format(endOfQuarter(date), "yyyy-MM-dd"),
        label: `Q${fiscal} ${+year + yearOffset}`,
        index,
      };
    });

  // remove this when pinnacle Q2 2022 will be ready
  const hide =
    domain === "pinnacle" && activeTab?.slug?.includes(pagesWithExcelence);
  // remove this when pinnacle Q2 2022 will be ready
  return options.filter((option) => {
    if (hide && !isStagingEnv) {
      return !ignore.includes(option.label);
    }

    return true;
  });
}

export function getFullWeekDateRange(includeFullWeek, dateFilters) {
  const { start, end } = dateFilters;

  // if !includeFullWeek we will use user selected Date
  if (!includeFullWeek || !start?.value || !end?.value) {
    return dateFilters;
  }

  return {
    start: {
      ...start,
      value: format(startOfWeek(parseISO(start.value)), "yyyy-MM-dd"),
    },
    end: {
      ...end,
      value: format(endOfWeek(parseISO(end.value)), "yyyy-MM-dd"),
    },
  };
}

export function getLastWeekRange() {
  return {
    start: format(startOfWeek(getLastWeekDate()), "yyyy-MM-dd"),
    end: format(endOfWeek(getLastWeekDate()), "yyyy-MM-dd"),
  };
}

/**
 * Offsets a local date based on the passed timezone (taking DST into account).
 * The resulting date is also in the local date format like the passed date, so
 * it can only be used for operations that are agnostic of the timezone.
 *
 * @param date {Date}
 * @param timezone {string} E.g. "America/New_York"
 * @returns {Date}
 */
export function offsetDateByTimezone(date, timezone) {
  const withTimezone = date.toLocaleString("en-US", {
    timeZone: timezone,
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });

  return new Date(convertUSDateStringToISODate(withTimezone));
}

/**
 * Converts an en-US locale date string into an ISO string.
 * NOTE: the resulting string does not contain any timezone information.
 *  Constructing a new Date with it will be in the user's local timezone.
 *
 * @param usDateString {string}
 * @returns {string}
 */
export function convertUSDateStringToISODate(usDateString) {
  const s = usDateString.split(/\D+/);
  return `${s[2]}-${s[0]}-${s[1]}T${s[3]}:${s[4]}:${s[5]}`;
}

/**
 * Returns New York's yesterday's date string.
 *
 * @param date {Date?}
 * @returns {string}
 */
export const setSpecificTimezone = (date = getDate()) => {
  const newDate = addDays(offsetDateByTimezone(date, "America/New_York"), -1);

  return format(newDate, "yyyy-MM-dd");
};

export const getWeekNameByWeekRange = (weekRange = "", weekOffset) => {
  // const date = weekRange.split("-")[0];
  // const diff = differenceInWeeks(new Date(date), startOfWeek(getNow()));
  // We were checking by diff above earlier
  switch (weekOffset) {
    case -1:
      return weekRange + " - Last Week";

    case 0:
      return weekRange + " - Current Week";

    case 1:
      return weekRange + " - Next Week";

    case 2:
      return weekRange + " - Two Weeks Out";

    default:
      return weekRange;
  }
};

export function isDate(date) {
  return date && new Date(date) !== "Invalid Date" && !isNaN(new Date(date));
}

export function getFiscalYear(date, monthOffset = 0) {
  const simpleDateString = convertFromUSLocale(date);
  const toDate = parseISO(simpleDateString);

  const month = toDate.getMonth();
  const year = toDate.getFullYear();

  const yearOffset = Math.floor((month - (monthOffset % 12 || 12)) / 12) + 1;
  return yearOffset + year;
}

function convertFromUSLocale(str) {
  const dateArr = str.split("/");

  if (dateArr.length === 3) {
    const [month, day, year] = dateArr;
    return `${year}-${month}-${day}`;
  }

  return str;
}

const getApprovedPeriod = (options, dateType, dateFiltersConfig) => {
  const { approved, defaultFiscalQuarter } = dateFiltersConfig;

  if ((!approved || !approved[dateType]) && !defaultFiscalQuarter) {
    return 1;
  }

  const index = options.findIndex(
    (o) =>
      (approved && o.label === approved[dateType]) ||
      o.label === defaultFiscalQuarter
  );

  return index > -1 ? index : 1;
};

export const setDynamicDateOption = ({
  options,
  dateType,
  dateFilterSettings = {},
  dateFiltersConfig = {},
}) => {
  const specialGroup = [
    "rm",
    "ry",
    "rq",
    "singleFiscalQuarter",
    "fiscalQuarterRange",
  ];

  const { startDate, endDate, term } = dateFilterSettings ?? {};

  if (term === dateType) {
    return options.find(
      (option) => option.from === startDate && option.to === endDate
    );
  }

  if (specialGroup.includes(dateType)) {
    const index = getApprovedPeriod(options, dateType, dateFiltersConfig);
    return options[index];
  }

  return options[0];
};

export const getCustomRangeDates = () => {
  const yesterday = startOfYesterday();
  const firstDayOfMonth = startOfMonth(yesterday);

  return {
    start: format(firstDayOfMonth, "yyyy-MM-dd"),
    end: format(yesterday, "yyyy-MM-dd"),
  };
};

export const getLatestCompleteTenTwoWeeks = (approved = {}) => {
  const currentDate = new Date();
  const biweeklySchedule = approved.biweeklySchedule ?? addDays(currentDate, 1);

  const fromDate = new Date(biweeklySchedule);
  const recentWeekEnd = getRecentWeekEndDate(currentDate, fromDate);

  return createBiweeklyOptions(recentWeekEnd);
};

function getRecentWeekEndDate(currentDate, fromDate) {
  const difference = differenceInWeeks(currentDate, fromDate);
  return addWeeks(fromDate, difference);
}

function createBiweeklyOptions(date) {
  const period = [];

  for (let i = 1; i <= 10; i++) {
    const startDate = subWeeks(date, i * 2);
    const endDate = subDays(addWeeks(startDate, 2), 1);

    const from = format(startDate, "yyyy-MM-dd");
    const to = format(endDate, "yyyy-MM-dd");

    const label = `${format(startDate, "MM/dd/yyyy")} - ${format(
      endDate,
      "MM/dd/yyyy"
    )}`;

    period.push({ from, to, label });
  }

  return period;
}
